import makeStyles from '@mui/styles/makeStyles';
import {isEmpty} from 'lodash';
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation} from "react-router-dom";
import {isEmailValid} from "common/helpers";
import {color, font, type} from "common/tokens";
import useBusy from "common/useBusy";
import OTP from "components/OTP";
import Question from 'components/Question/index';
import AuthLayout from "components/redesign/AuthLayout";
import CtaButton from "components/redesign/CtaButton";
import PasswordRules, {meetsPasswordPolicy} from "components/redesign/PasswordRules";

const useStyles = makeStyles({
  title: {
    ...type.panelTitle,
    color: color.TEXT,
    margin: 0,
    textAlign: 'center',
  },
  subtitle: {
    ...type.body,
    color: color.TEXT_MUTED,
    margin: '6px 0 20px',
    textAlign: 'center',
  },
  gap: {
    height: 20,
  },
  progressTrack: {
    height: 4,
    borderRadius: 4,
    background: color.DIVIDER,
    overflow: 'hidden',
    margin: '0 0 20px',
  },
  progressFill: {
    display: 'block',
    height: '100%',
    borderRadius: 4,
    background: color.ACCENT,
  },
  error: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.DANGER,
    textAlign: 'center',
    margin: '14px 0 0',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    // Questions space themselves 28px apart for a survey; a short auth form
    // reads better tighter.
    '& > div': {
      marginBottom: 18,
    },
  },
});

const STEPS = 3;

const ResetPassword = ({
  validateEmailAddress,
  resetPasswordScreen = 0,
  emailOtpError,
  otpMessage,
  resetPasswordOtpVerify,
  otpVerificationStatus,
  setNewPassword,
  otpError,
}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, run] = useBusy();

  // Arriving from a mail link, the address is already known.
  useEffect(() => {
    const prefilled = new URLSearchParams(location.search).get('email');
    if (prefilled) setEmail(prefilled);
  }, [location]);

  const handleEmailSubmit = event => {
    if (event && event.preventDefault) event.preventDefault();

    if (isEmailValid(email.toLowerCase())) {
      setEmailError(false);
      run(() => validateEmailAddress(email));
    } else {
      setEmailError(true);
    }
  };

  const passwordsMatch = !isEmpty(confirmPassword) && password === confirmPassword;
  const canSetPassword = meetsPasswordPolicy(password) && passwordsMatch;

  const steps = [
    (
      <>
        <h1 className={classes.title}>{t('resetPasswordEmailTitle')}</h1>
        <p className={classes.subtitle}>{t('resetPasswordEmailHelpText')}</p>
        <form className={classes.form} onSubmit={handleEmailSubmit}>
          <Question
            question={{label: t('emailLabel'), questionType: 'Text', autocomplete: 'email', mandatory: true}}
            onAnswered={value => setEmail(value.toLowerCase())}
            value={email}
            error={emailError && t('invalidEmail')}
          />
          <CtaButton type={'submit'} size={'large'} fullWidth label={t('sendOTP')} loading={busy}/>
        </form>
        {emailOtpError && <p className={classes.error} role={'alert'}>{emailOtpError}</p>}
      </>
    ),
    (
      <>
        <h1 className={classes.title}>{t('enterOTPToContinue')}</h1>
        <p className={classes.subtitle}>{`${t('sentOTP')} ${email}`}</p>
        <OTP
          message={otpMessage}
          onAnswered={otp => run(() => resetPasswordOtpVerify(email, otp))}
          resendOtp={handleEmailSubmit}
          error={!otpVerificationStatus}
          otpErrorMessage={otpError}
          currentStep={resetPasswordScreen === 1}
          numInputs={6}
          loading={busy}
        />
      </>
    ),
    (
      <>
        <h1 className={classes.title}>{t('resetPasswordPasswordTitle')}</h1>
        <div className={classes.gap}/>
        <form
          className={classes.form}
          onSubmit={event => {
            event.preventDefault();
            if (canSetPassword) run(() => setNewPassword(password));
          }}
        >
          <Question
            question={{label: t('resetPasswordPasswordLabel'), questionType: 'Password', autocomplete: 'new-password', mandatory: true}}
            onAnswered={setPassword}
            value={password}
          />
          <PasswordRules password={password}/>
          <Question
            question={{label: t('confirmPasswordLabel'), questionType: 'Password', autocomplete: 'new-password', mandatory: true}}
            onAnswered={setConfirmPassword}
            value={confirmPassword}
            error={!isEmpty(confirmPassword) && !passwordsMatch && t('passwordsDontMatch')}
          />
          <CtaButton type={'submit'} size={'large'} fullWidth label={t('continueCaps')} disabled={!canSetPassword} loading={busy}/>
        </form>
      </>
    ),
  ];

  return (
    <AuthLayout>
      <div
        className={classes.progressTrack}
        role={'progressbar'}
        aria-label={t('resetPassword')}
        aria-valuenow={resetPasswordScreen + 1}
        aria-valuemin={1}
        aria-valuemax={STEPS}
      >
        <span
          className={classes.progressFill}
          style={{width: `${((resetPasswordScreen + 1) * 100) / STEPS}%`}}
        />
      </div>
      {steps[resetPasswordScreen] || steps[0]}
    </AuthLayout>
  );
};

export default ResetPassword;
